import { html } from "lit";
import type { RouteConfig } from "@lit-labs/router";

import { pb, hasOrganization } from "./lib/pb";
import { navigate } from "./lib/navigation";

import "./pages/login-page";
import "./pages/signup-page";
import "./pages/reset-password-page";
import "./pages/onboarding-page";
import "./pages/dashboard-page";
import "./pages/products-page";
import "./pages/product-detail-page";
import "./pages/nfc-page";
import "./pages/gi-page";
import "./pages/members-page";
import "./pages/audit-page";
import "./pages/security-page";
import "./pages/recall-page";
import "./pages/assistance-page";
import "./pages/ingest-page";
import "./pages/inspection-page";
import "./pages/not-found-page";

type Guard = () => Promise<boolean>;

/** Only reachable when signed out; signed-in users are sent to their home route. */
const requireGuest: Guard = async () => {
  if (pb.authStore.isValid) {
    await navigate(hasOrganization() ? "/" : "/onboarding");
    return false;
  }
  return true;
};

/**
 * Bouncing a signed-out user to /login used to DROP the URL they arrived on —
 * and the tokened deep links (an emailed invitation, a shared record) are
 * exactly the URLs a signed-out user arrives on. Stash the full intended URL
 * so login/signup can land them back where the link pointed; sessionStorage,
 * not localStorage, so it dies with the tab. consumeReturnTo (below) is the
 * other half.
 */
const stashReturnTo = (): void => {
  sessionStorage.setItem("returnTo", location.pathname + location.search);
};

/**
 * The stashed URL, validated and cleared. Only a same-origin absolute path is
 * ever honoured — anything else (a full URL, a protocol-relative //host) would
 * make the stash an open redirect.
 */
export const consumeReturnTo = (): string | null => {
  const rt = sessionStorage.getItem("returnTo");
  sessionStorage.removeItem("returnTo");
  if (rt && rt.startsWith("/") && !rt.startsWith("//")) return rt;
  return null;
};

/** Requires a signed-in user (organization optional — used by /onboarding). */
const requireAuth: Guard = async () => {
  if (!pb.authStore.isValid) {
    stashReturnTo();
    await navigate("/login");
    return false;
  }
  return true;
};

/** Requires a signed-in user that has completed onboarding. */
const requireOrg: Guard = async () => {
  if (!pb.authStore.isValid) {
    stashReturnTo();
    await navigate("/login");
    return false;
  }
  if (!hasOrganization()) {
    await navigate("/onboarding");
    return false;
  }
  return true;
};

export const routes: RouteConfig[] = [
  {
    path: "/login",
    enter: requireGuest,
    render: () => html`<login-page></login-page>`,
  },
  {
    path: "/signup",
    enter: requireGuest,
    render: () => html`<signup-page></signup-page>`,
  },
  {
    // No guest guard: a signed-in user following an emailed reset link must
    // still land on the form, not be bounced to the dashboard.
    path: "/reset-password",
    render: () => html`<reset-password-page></reset-password-page>`,
  },
  {
    path: "/onboarding",
    enter: requireAuth,
    render: () => html`<onboarding-page></onboarding-page>`,
  },
  {
    path: "/",
    enter: requireOrg,
    render: () => html`<dashboard-page></dashboard-page>`,
  },
  {
    path: "/products",
    enter: requireOrg,
    render: () => html`<products-page></products-page>`,
  },
  {
    path: "/products/:id",
    enter: requireOrg,
    render: ({ id }) =>
      html`<product-detail-page
        .productId=${id ?? ""}
      ></product-detail-page>`,
  },
  {
    path: "/nfc",
    enter: requireOrg,
    render: () => html`<nfc-page></nfc-page>`,
  },
  {
    path: "/gi",
    enter: requireOrg,
    render: () => html`<gi-page></gi-page>`,
  },
  {
    path: "/security",
    enter: requireOrg,
    render: () => html`<security-page></security-page>`,
  },
  {
    path: "/members",
    enter: requireOrg,
    render: () => html`<members-page></members-page>`,
  },
  {
    path: "/audit",
    enter: requireOrg,
    render: () => html`<audit-page></audit-page>`,
  },
  {
    path: "/recall",
    enter: requireOrg,
    render: () => html`<recall-page></recall-page>`,
  },
  {
    path: "/assistance",
    enter: requireOrg,
    render: () => html`<assistance-page></assistance-page>`,
  },
  {
    path: "/ingest",
    enter: requireOrg,
    render: () => html`<ingest-page></ingest-page>`,
  },
  {
    // Auth only, not org: an inspector is cross-tenant by design; the page
    // itself gates on the role.
    path: "/inspection",
    enter: requireAuth,
    render: () => html`<inspection-page></inspection-page>`,
  },
];

/** Always-matches fallback for unknown paths. */
export const fallbackRoute = {
  render: () => html`<not-found-page></not-found-page>`,
};
